/**
 * custom-variant-fixes.js
 * WebArt custom module for Spindrift Motorsport (Prestige theme)
 *
 * TASK 1: Forces the customer to explicitly click every option before
 *         "Add to Basket" becomes clickable/submittable.
 *
 * TASK 2: Hides variant option values that don't exist as a real variant
 *         combination. Requires data-unavailable="true" on the badge span -
 *         see the one-line companion edit to snippets/variant-picker.liquid.
 *
 * IMPORTANT (why this version looks the way it does): Prestige re-renders
 * (swaps) the <variant-picker> markup after every option change, because of
 * the "update-url" attribute on main product pickers. Earlier versions of
 * this script directly overwrote the displayed value text and unchecked the
 * default radio - that fought with the theme's own async re-render and
 * caused a visible flash on load and a stuck placeholder after the first
 * click. This version never touches the theme's real value text or checked
 * state at all. Instead it layers a pure CSS/class overlay on top ("please
 * select an option") for options not yet explicitly clicked, and removes it
 * the moment the user clicks - so the real, theme-managed value underneath
 * is always shown correctly and immediately, with no race condition.
 *
 * Does not modify theme.js or the VariantPicker class.
 * Loaded from theme.liquid as a standalone <script type="module">.
 */

(() => {
  const REVEALED_CLASS = 'variant-guard-revealed';
  const DISABLED_CLASS = 'is-disabled-by-variant-guard';
  const ATTENTION_CLASS = 'variant-guard-attention';

  // formId -> { selectedPositions: Set<string>, requiredCount: number }
  const formStates = new Map();

  function getState(formId) {
    if (!formStates.has(formId)) {
      formStates.set(formId, { selectedPositions: new Set(), requiredCount: 0 });
    }
    return formStates.get(formId);
  }

  /* -----------------------------------------------------------------
   * Styles: disabled button + pure-CSS placeholder overlay
   * ----------------------------------------------------------------- */
  // Note: the "hide real value / show placeholder" CSS is intentionally
  // NOT injected here. It lives as blocking inline CSS in theme.liquid's
  // <head> so it's active before first paint (no flash). This function only
  // adds the small stylesheet needed for the disabled button state.
  function injectStyles() {
    if (document.getElementById('variant-guard-styles')) return;
    const style = document.createElement('style');
    style.id = 'variant-guard-styles';
    style.textContent = `
      .${DISABLED_CLASS} { opacity: 0.5; cursor: not-allowed; }
      .${ATTENTION_CLASS} { box-shadow: 0 0 0 2px #00b0a6 !important; border-radius: inherit; position: relative; z-index: 5; transition: box-shadow 0.15s ease; }
    `;
    document.head.appendChild(style);
  }

  /* -----------------------------------------------------------------
   * TASK 2: hide option values that don't exist as a real variant
   * ----------------------------------------------------------------- */
  function hideUnavailableOptionValues(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-unavailable="true"]').forEach((badge) => {
      const wrapper = badge.closest('.popover__value-option');
      if (wrapper) wrapper.style.display = 'none';
    });
  }

  function watchForUnavailableBadges() {
    hideUnavailableOptionValues(document);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-unavailable="true"]')) {
            hideUnavailableOptionValues(node.closest('.popover__value-list') || document);
          } else {
            hideUnavailableOptionValues(node);
          }
        });
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* -----------------------------------------------------------------
   * TASK 1: require an explicit click on every option
   * ----------------------------------------------------------------- */
  function getSubmitButton(formId) {
    if (!formId) return null;
    const formEl = document.getElementById(formId);
    if (!formEl) return null;

    const buyButtons = formEl.querySelector('buy-buttons') || document.querySelector(`buy-buttons[form="${CSS.escape(formId)}"]`);
    if (buyButtons) {
      const btn = buyButtons.querySelector('button');
      if (btn) return btn;
    }

    let btn = formEl.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) return btn;

    btn = document.querySelector(`[form="${CSS.escape(formId)}"][type="submit"]`);
    if (btn) return btn;

    return null;
  }

  function applyButtonState(btn, enabled) {
    if (!btn) return;
    // Deliberately NOT setting btn.disabled here. A genuinely disabled
    // button never dispatches a "click" event at all, which meant our own
    // blockIncompleteSubmits handler (and the highlight it triggers) never
    // fired when someone tapped it. Blocking is instead handled entirely
    // by intercepting the click/submit events - this class is visual only.
    btn.setAttribute('aria-disabled', String(!enabled));
    btn.classList.toggle(DISABLED_CLASS, !enabled);
  }

  function guardButton(formId) {
    const state = getState(formId);
    const btn = getSubmitButton(formId);
    if (!btn) return;

    const complete = state.selectedPositions.size >= state.requiredCount;
    applyButtonState(btn, complete);

    if (!btn.__variantGuardObserved) {
      btn.__variantGuardObserved = true;
      const observer = new MutationObserver(() => {
        const s = getState(formId);
        const stillComplete = s.selectedPositions.size >= s.requiredCount;
        if (!stillComplete && !btn.classList.contains(DISABLED_CLASS)) {
          applyButtonState(btn, false);
        }
      });
      observer.observe(btn, { attributes: true, attributeFilter: ['class', 'aria-disabled'] });
    }
  }

  // Returns this fieldset's option position (1-indexed), derived from its
  // order among sibling option fieldsets - NOT from a data-option-position
  // attribute nested inside the popover. That attribute only lives on the
  // radio inputs inside the popover's value list, and that content is
  // clearly torn down/absent once the popover is closed (confirmed via
  // diagnostics: fieldset count matched, but nested attributes were gone).
  // DOM order is static and always available, and matches Shopify's own
  // 1-indexed option.position convention for options_with_values.
  function fieldsetPosition(fieldset) {
    const picker = fieldset.closest('variant-picker');
    if (!picker) return null;
    const siblings = Array.from(picker.querySelectorAll(':scope > fieldset.variant-picker__option'));
    const idx = siblings.indexOf(fieldset);
    return idx === -1 ? null : String(idx + 1);
  }

  // Resolves which <variant-picker>/form a tapped option belongs to. On
  // desktop the popover stays inline inside <variant-picker>, so a simple
  // closest() works. On mobile, Prestige renders the option list as a
  // portaled/bottom-sheet element appended elsewhere in the DOM (outside
  // <variant-picker>), so we fall back to resolving it via the popover's
  // own id -> its trigger button (which never moves) -> that button's
  // <variant-picker>.
  function resolveFormId(input) {
    const picker = input.closest('variant-picker');
    if (picker) return picker.getAttribute('form-id');

    const popover = input.closest('x-popover');
    if (popover && popover.id) {
      const trigger = document.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`);
      const triggerPicker = trigger ? trigger.closest('variant-picker') : null;
      if (triggerPicker) return triggerPicker.getAttribute('form-id');
    }

    return null;
  }

  // Force-closes the option popover after a selection is made. Some
  // interactions weren't reliably triggering the theme's own
  // "close-on-listbox-change" behaviour, leaving the list open until the
  // shopper clicked elsewhere. This gives the theme's own handling a brief
  // head start, then closes it directly via every mechanism it's likely to
  // understand (native Popover API, Escape key, aria-expanded).
  function closePopover(fromEl) {
    const popover = fromEl.closest('x-popover');
    if (!popover) return;

    setTimeout(() => {
      if (typeof popover.hidePopover === 'function') {
        try {
          popover.hidePopover();
        } catch (e) {
          /* not using the native Popover API - ignore */
        }
      }

      popover.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true })
      );

      if (popover.id) {
        const trigger = document.querySelector(`[aria-controls="${CSS.escape(popover.id)}"]`);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      }
    }, 50);
  }

  // Pure visual sync - never touches the theme's real value text, just adds
  // a "revealed" class once an option has been explicitly selected.
  // Everything else stays hidden behind the critical CSS in theme.liquid by
  // default, so there's nothing to flash on load. Also clears the
  // attention-highlight (issue 3) once an option is revealed, and uses
  // clearUnselectedDefaultChecks (issue 2) to keep the popover from
  // pre-highlighting a default value the shopper never actually chose.
  function syncRevealedClasses(picker, state) {
    const optionFieldsets = Array.from(picker.querySelectorAll(':scope > fieldset.variant-picker__option'));
    optionFieldsets.forEach((fieldset) => {
      const position = fieldsetPosition(fieldset);
      const revealed = position ? state.selectedPositions.has(position) : false;
      fieldset.classList.toggle(REVEALED_CLASS, revealed);
      if (revealed) {
        const trigger = fieldset.querySelector('.relative > button.select');
        if (trigger) trigger.classList.remove(ATTENTION_CLASS);
      }
    });
    return optionFieldsets;
  }

  // ISSUE 1: if changing one option makes the current combination invalid
  // (no matching variant), reset the OTHER already-selected option(s) back
  // to "Please select an option..." instead of leaving them showing a value
  // that no longer forms a real combination.
  function checkVariantAvailability(picker, state) {
    if (state.selectedPositions.size < 2 || !state.lastPosition) return;

    const script = picker.querySelector('script[data-variant]');
    if (!script) return;

    let variant;
    try {
      variant = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }

    if (variant !== null) return;

    const keep = state.lastPosition;
    let changed = false;
    Array.from(state.selectedPositions).forEach((pos) => {
      if (pos !== keep) {
        state.selectedPositions.delete(pos);
        changed = true;
      }
    });

    if (changed) syncRevealedClasses(picker, state);
  }

  // ISSUE 2: the theme leaves its own default-preselected radio :checked
  // even though we hide/placeholder the button text, so opening a dropdown
  // that hasn't been explicitly chosen yet still shows that default value
  // highlighted in the list. Since Task 1's selection detection is based on
  // DOM structure/order (not on checked state), it's safe to uncheck any
  // default that belongs to a not-yet-selected option - purely cosmetic,
  // doesn't affect selection logic.
  function maybeUncheckDefault(radio) {
    const formId = resolveFormId(radio);
    if (!formId) return;
    const state = getState(formId);
    const position = radio.getAttribute('data-option-position');
    if (position && !state.selectedPositions.has(position)) {
      radio.checked = false;
    }
  }

  function clearUnselectedDefaultChecks(node) {
    if (!node) return;
    if (node.matches && node.matches('input[type="radio"]:checked[data-option-position]')) {
      maybeUncheckDefault(node);
    }
    if (node.querySelectorAll) {
      node.querySelectorAll('input[type="radio"]:checked[data-option-position]').forEach(maybeUncheckDefault);
    }
  }

  // Self-heals the "revealed" state. Prestige appears to re-render the
  // picker via in-place DOM morphing (patching the existing element rather
  // than replacing it) whenever one option changes, so it can recompute
  // which values are available for the OTHER options. That morph resets
  // each fieldset's class attribute to match the freshly rendered server
  // HTML, which silently wipes our "variant-guard-revealed" class off an
  // already-selected option. This watches for exactly that and reapplies
  // the class immediately if it gets removed while it shouldn't have been.
  function watchFieldsetRevealState(picker, formId) {
    if (picker.__variantGuardRevealObserved) return;
    picker.__variantGuardRevealObserved = true;

    const observer = new MutationObserver((mutations) => {
      const state = getState(formId);
      let needsResync = false;

      for (const mutation of mutations) {
        const fieldset = mutation.target.closest && mutation.target.closest('fieldset.variant-picker__option');
        if (!fieldset) continue;

        const position = fieldsetPosition(fieldset);
        const shouldBeRevealed = position && state.selectedPositions.has(position);
        const isRevealed = fieldset.classList.contains(REVEALED_CLASS);

        if (shouldBeRevealed && !isRevealed) {
          needsResync = true;
        }
      }

      if (needsResync) {
        syncRevealedClasses(picker, state);
      }
    });

    observer.observe(picker, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  const availabilityDebounceTimers = new Map();

  // Debounces the availability check so a multi-step theme update (which
  // may pass through a transient "no variant matches yet" state before
  // settling on the real final value) doesn't get evaluated mid-flight and
  // cause an incorrect cascade reset.
  function scheduleAvailabilityCheck(picker, formId) {
    clearTimeout(availabilityDebounceTimers.get(formId));
    const timer = setTimeout(() => {
      checkVariantAvailability(picker, getState(formId));
    }, 180);
    availabilityDebounceTimers.set(formId, timer);
  }

  // Watches the theme's own <script data-variant> JSON directly so an
  // invalid-combination reset (see checkVariantAvailability) fires the
  // instant the theme updates that data, instead of waiting for the next
  // poll tick or happening to be triggered by an unrelated fieldset swap.
  function watchVariantScript(picker, formId) {
    const script = picker.querySelector('script[data-variant]');
    if (!script || script.__variantGuardScriptObserved) return;
    script.__variantGuardScriptObserved = true;

    const observer = new MutationObserver(() => {
      scheduleAvailabilityCheck(picker, formId);
    });
    observer.observe(script, { characterData: true, childList: true, subtree: true });
  }

  function initVariantPicker(picker) {
    const formId = picker.getAttribute('form-id');
    if (!formId) return;

    const optionFieldsets = Array.from(picker.querySelectorAll(':scope > fieldset.variant-picker__option'));
    if (optionFieldsets.length === 0) return;

    const state = getState(formId);
    state.requiredCount = optionFieldsets.length;

    syncRevealedClasses(picker, state);
    watchFieldsetRevealState(picker, formId);
    watchVariantScript(picker, formId);
    guardButton(formId);

    const formEl = document.getElementById(formId);
    if (formEl && !formEl.__variantGuardSubmitBound) {
      formEl.__variantGuardSubmitBound = true;
      formEl.addEventListener(
        'submit',
        (event) => {
          const s = getState(formId);
          if (s.selectedPositions.size < s.requiredCount) {
            event.preventDefault();
            event.stopPropagation();
            highlightPendingOptions(formId);
          }
        },
        true
      );
    }
  }

  function initAllVariantPickers(root = document) {
    root.querySelectorAll('variant-picker').forEach(initVariantPicker);
  }

  // Re-syncs a single already-initialised picker (as opposed to
  // initVariantPicker, which also does first-time setup like binding the
  // submit guard). Used when only a fieldset was swapped, not the whole
  // <variant-picker>.
  function resyncPicker(picker) {
    const formId = picker.getAttribute('form-id');
    if (!formId) return;
    const state = getState(formId);
    syncRevealedClasses(picker, state);
    watchVariantScript(picker, formId);
    scheduleAvailabilityCheck(picker, formId);
    guardButton(formId);
  }

  function watchForVariantPickerChanges() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;

          // Popover option lists are rendered on open (and torn down on
          // close), so this is where a default-checked radio for a
          // not-yet-selected option first becomes visible - catch it here.
          clearUnselectedDefaultChecks(node);

          if (node.tagName === 'VARIANT-PICKER') {
            initVariantPicker(node);
            return;
          }

          // The theme sometimes swaps individual option <fieldset> elements
          // in place, leaving the parent <variant-picker> itself untouched.
          // That's neither a new <variant-picker> node nor an attribute
          // change on an existing node, so it needs its own check.
          if (node.matches && node.matches('fieldset.variant-picker__option')) {
            const picker = node.closest('variant-picker');
            if (picker) resyncPicker(picker);
            return;
          }

          // Same idea for the variant data script tag itself, if the theme
          // replaces that node wholesale rather than mutating its content.
          if (node.matches && node.matches('script[data-variant]')) {
            const picker = node.closest('variant-picker');
            if (picker) resyncPicker(picker);
            return;
          }

          if (node.querySelectorAll) {
            node.querySelectorAll('variant-picker').forEach(initVariantPicker);
            node.querySelectorAll('fieldset.variant-picker__option').forEach((fieldset) => {
              const picker = fieldset.closest('variant-picker');
              if (picker) resyncPicker(picker);
            });
            node.querySelectorAll('script[data-variant]').forEach((script) => {
              const picker = script.closest('variant-picker');
              if (picker) resyncPicker(picker);
            });
          }
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Registers a real user selection - both an actual value change AND a
  // click on the currently-already-checked default option (which won't
  // fire "change" on its own, since nothing changed from the browser's
  // point of view).
  function registerSelection(formId, position) {
    if (!formId || !position) return;
    const state = getState(formId);
    state.lastPosition = position;

    const isNewPosition = !state.selectedPositions.has(position);
    if (isNewPosition) state.selectedPositions.add(position);

    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    if (picker) {
      if (isNewPosition) syncRevealedClasses(picker, state);
      // Debounced (not called directly) - the theme's variant data hasn't
      // updated yet at this exact synchronous moment, so checking it
      // immediately would see stale data from before this change.
      scheduleAvailabilityCheck(picker, formId);
    }

    guardButton(formId);
  }

  function watchForUserSelections() {
    // All listeners run in the CAPTURE phase (the "true" third argument).
    // That means they fire on the way down, before the theme's own click /
    // touch handling (wherever it's attached) gets a chance to run - so
    // even if the theme removes or replaces the tapped option's markup as
    // part of its own handling, we've already read the position we need.
    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input || input.tagName !== 'INPUT' || input.type !== 'radio') return;
        const formId = resolveFormId(input);
        if (!formId) return;
        registerSelection(formId, input.getAttribute('data-option-position'));
        closePopover(input);
      },
      true
    );

    // Primary path for touch devices, and fallback for re-selecting a value
    // that was already checked (no "change" event fires for that case).
    const handleOptionActivation = (event) => {
      const optionRow = event.target.closest('.popover__value-option');
      if (!optionRow) return;
      const input = optionRow.querySelector('[data-option-position]');
      if (!input) return;
      const formId = resolveFormId(input);
      if (!formId) return;
      registerSelection(formId, input.getAttribute('data-option-position'));
      closePopover(optionRow);
    };

    document.addEventListener('click', handleOptionActivation, true);
    document.addEventListener('touchend', handleOptionActivation, { capture: true, passive: true });
  }

  // Safety net: continuously re-asserts the correct revealed/button state
  // for every tracked form, regardless of how or where the theme changed
  // the DOM. The mutation observers above handle the common cases
  // instantly; this guarantees convergence within ~200ms even for update
  // paths we haven't specifically accounted for.
  function startResyncLoop() {
    setInterval(() => {
      formStates.forEach((state, formId) => {
        const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
        if (picker) {
          syncRevealedClasses(picker, state);
          scheduleAvailabilityCheck(picker, formId);
        }
        guardButton(formId);
      });
    }, 200);
  }

  // ISSUE 3: highlights each not-yet-selected dropdown in the brand teal.
  // Stays highlighted until that specific option is actually selected
  // (cleared automatically by syncRevealedClasses once revealed=true) -
  // not on a timer, so it keeps guiding the shopper until they act on it.
  function highlightPendingOptions(formId) {
    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    if (!picker) return;
    const state = getState(formId);

    Array.from(picker.querySelectorAll(':scope > fieldset.variant-picker__option')).forEach((fieldset) => {
      const position = fieldsetPosition(fieldset);
      if (position && state.selectedPositions.has(position)) return;

      const trigger = fieldset.querySelector('.relative > button.select');
      if (!trigger) return;

      trigger.classList.add(ATTENTION_CLASS);
    });
  }

  // Hard block: prevents the click from ever reaching the theme's own
  // handlers, regardless of how "Add to Basket" is wired internally.
  function blockIncompleteSubmits() {
    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest('[type="submit"], buy-buttons button');
        if (!btn) return;

        const formId = btn.getAttribute('form') || btn.closest('form')?.id;
        if (!formId || !formStates.has(formId)) return;

        const state = getState(formId);
        if (state.selectedPositions.size < state.requiredCount) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          highlightPendingOptions(formId);
        }
      },
      true
    );
  }

  /* -----------------------------------------------------------------
   * Boot
   * ----------------------------------------------------------------- */
  injectStyles();
  initAllVariantPickers();
  clearUnselectedDefaultChecks(document);
  watchForVariantPickerChanges();
  watchForUserSelections();
  blockIncompleteSubmits();
  watchForUnavailableBadges();
  startResyncLoop();
})();